import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Trophy } from "lucide-react";
import { TROPHY_DETAILS, findTrophyBySlug } from "@/data/trophies";
import useSEO, { BASE_URL } from "@/hooks/useSEO";

export default function TrophyDetail() {
  const { slug } = useParams();
  const trophy = findTrophyBySlug(slug);

  useSEO({
    title: trophy
      ? `${trophy.title} · ${trophy.year} · La Bombonera`
      : "Título · La Bombonera",
    description: trophy
      ? `${trophy.cat} · ${trophy.date}. ${trophy.intro.slice(0, 150)}`
      : "Historia de los títulos del C.D. La Bombonera de Barcelona.",
    canonical: trophy ? `${BASE_URL}/titulos/${trophy.slug}` : undefined,
    image: trophy?.cover,
  });

  if (!trophy) {
    return <Navigate to="/titulos" replace />;
  }

  const idx = TROPHY_DETAILS.findIndex((t) => t.slug === slug);
  const prev = idx > 0 ? TROPHY_DETAILS[idx - 1] : null;
  const next =
    idx < TROPHY_DETAILS.length - 1 ? TROPHY_DETAILS[idx + 1] : null;

  return (
    <main data-testid="trophy-detail-page" className="pt-32 pb-24">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-16">
        <Link
          to="/titulos"
          data-testid="trophy-back-link"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/55 hover:text-[#FFB800] transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Volver al palmarés
        </Link>

        {/* Ambient portrait card — blurred cover as backdrop */}
        <div className="relative rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-[#0A1121] to-[#030712] p-6 md:p-10 lg:p-14 overflow-hidden">
          <img
            src={trophy.cover}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-35 pointer-events-none"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#030712]/85 via-[#030712]/75 to-[#030712]/90 pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,184,0,0.08),transparent_60%)] pointer-events-none" />

          <div className="relative">
            {/* Header */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="rounded-full bg-[#FFB800] text-[#030712] px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] font-bold flex items-center gap-2">
                <Trophy size={12} strokeWidth={2.5} /> {trophy.year}
              </span>
              <span className="rounded-full border border-white/15 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-white/70">
                {trophy.cat}
              </span>
              <span className="rounded-full border border-white/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                {trophy.date}
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight leading-[0.95]">
              {trophy.title}
            </h1>

            {/* Cover */}
            <div className="relative mt-12 rounded-[2rem] overflow-hidden bg-[#0A1121] aspect-[16/9] ring-1 ring-white/5">
              <img
                src={trophy.cover}
                alt={trophy.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                <p className="font-display text-xl md:text-3xl font-extrabold uppercase tracking-tight text-white max-w-2xl">
                  {trophy.headline}
                </p>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 bg-[#030712]/40 backdrop-blur rounded-full px-3 py-1.5 border border-white/10 hidden md:inline">
                  {String(idx + 1).padStart(2, "0")} / {String(TROPHY_DETAILS.length).padStart(2, "0")}
                </span>
              </div>
            </div>

            {/* Body */}
            <article className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8">
                <p className="text-xl md:text-2xl text-white leading-snug font-light">
                  {trophy.intro}
                </p>
                <div className="mt-10 space-y-6 text-white/75 text-[17px] leading-[1.8]">
                  {trophy.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                {trophy.images && trophy.images.length > 0 && (
                  <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {trophy.images.map((src, i) => (
                      <div
                        key={i}
                        className="rounded-[1.75rem] overflow-hidden bg-[#0A1121] aspect-[4/3]"
                      >
                        <img
                          src={src}
                          alt={`${trophy.title} ${i + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {trophy.credit && (
                  <p className="mt-12 text-[11px] uppercase tracking-[0.25em] text-white/40">
                    {trophy.credit}
                  </p>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-4 flex flex-col gap-6">
                {trophy.plantilla && (
                  <div className="rounded-[1.75rem] border border-white/10 p-7 bg-[#030712]/40 backdrop-blur-sm">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-5 flex items-center gap-3">
                      <span className="h-px w-8 bg-[#FFB800]" />
                      Plantilla
                    </p>
                    <ul className="flex flex-col divide-y divide-white/5">
                      {trophy.plantilla.map((p, i) => (
                        <li
                          key={i}
                          className="font-mono text-[12px] tracking-wide text-white/80 py-2"
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {trophy.scorers && (
                  <div className="rounded-[1.75rem] border border-[#FFB800]/30 p-7 bg-[#FFB800]/5 backdrop-blur-sm">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-5 flex items-center gap-3">
                      <span className="h-px w-8 bg-[#FFB800]" />
                      Goleadores
                    </p>
                    <ul className="flex flex-col gap-3">
                      {trophy.scorers.map((s, i) => (
                        <li
                          key={i}
                          className="font-display text-lg font-bold uppercase tracking-tight"
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </aside>
            </article>
          </div>
        </div>

        {/* Prev / Next */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-5">
          {prev ? (
            <Link
              to={`/titulos/${prev.slug}`}
              data-testid="trophy-prev-link"
              className="rounded-[1.75rem] border border-white/10 hover:border-[#FFB800]/50 p-6 transition-colors group"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 flex items-center gap-2">
                <ArrowLeft size={12} /> Anterior
              </p>
              <p className="font-display text-lg font-bold uppercase tracking-tight text-white group-hover:text-[#FFB800] transition-colors">
                {prev.title}
              </p>
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/titulos/${next.slug}`}
              data-testid="trophy-next-link"
              className="rounded-[1.75rem] border border-white/10 hover:border-[#FFB800]/50 p-6 transition-colors group text-right md:ml-auto md:w-full"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 flex items-center justify-end gap-2">
                Siguiente <ArrowUpRight size={12} />
              </p>
              <p className="font-display text-lg font-bold uppercase tracking-tight text-white group-hover:text-[#FFB800] transition-colors">
                {next.title}
              </p>
            </Link>
          )}
        </div>
      </div>
    </main>
  );
}
